import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
  OnDestroy
} from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { CommonLocalizationModule, LocalizationService } from '@capital-access/common/localization';
import {
  CommonUserModule,
  DefaultUserProviderModule,
  getAccount,
  UserRepository,
  ImpersonationService
} from '@capital-access/common/user';
import {
  FireflyModalService,
  FireflySingleSuggesterDropdownComponent,
  FireflySuggesterModule,
  NotificationService
} from '@capital-access/firefly/components';
import { COMMON_COMPONENTS_LOCALIZATION_SCOPE } from '../common-components.const';
import { ConsultantClientSelectorItem } from './models/consultant-client-selector.model';

@Component({
  selector: 'ca-consultants-client-selector',
  standalone: true,
  imports: [
    CommonModule,
    FireflySuggesterModule,
    CommonLocalizationModule,
    CommonUserModule,
    DefaultUserProviderModule
  ],
  providers: [],
  templateUrl: './consultants-client-selector.html',
  styleUrls: ['./consultants-client-selector.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ConsultantsClientSelectorComponent implements OnInit, OnDestroy {
  localizationScope = COMMON_COMPONENTS_LOCALIZATION_SCOPE;

  @ViewChild(FireflySingleSuggesterDropdownComponent) private suggester!: FireflySingleSuggesterDropdownComponent;

  suggesterClients$!: Observable<ConsultantClientSelectorItem[]>;
  selectedClient = signal<ConsultantClientSelectorItem | null>(null);

  tooltipPlacement = 'bottom-left';

  myAccountId: number | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private userRepository: UserRepository,
    private localizationService: LocalizationService,
    private modalService: FireflyModalService,
    private impersonationService: ImpersonationService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    const myAccount$ = this.store.select(getAccount);
    const myClients$ = this.userRepository.getConsultantClients();
    const labels$ = this.localizationService.localize(
      ['consultantsClientMyAccount', 'consultantsClientAvailableClients'],
      {},
      this.localizationScope
    );

    this.suggesterClients$ = combineLatest([myAccount$, myClients$, labels$]).pipe(
      takeUntil(this.destroy$),
      map(([myAccount, availableClients, [consultantsClientMyAccount, consultantsClientAvailableClients]]) => {
        const items: ConsultantClientSelectorItem[] = [];

        if (myAccount) {
          this.myAccountId = myAccount.id;
          this.selectedClient.set({
            id: myAccount.id,
            name: myAccount.name,
            category: consultantsClientMyAccount
          });
          items.push({
            id: myAccount.id,
            name: myAccount.name,
            category: consultantsClientMyAccount
          });
        }

        availableClients.forEach(client => {
          items.push({
            id: client.clientAccountId,
            name: client.clientAccountName,
            category: consultantsClientAvailableClients,
            dummyUserEmail: client.userEmail,
            dummyUserId: client.userId
          });
        });
        return items;
      })
    );
  }

  onSelect(event: unknown) {
    const selectedItem = event as ConsultantClientSelectorItem;
    // TODO: Only show modal if actually making a change

    this.showConfirmationModal(selectedItem).subscribe(confirmed => {
      if (confirmed) {
        if (selectedItem.id === this.myAccountId) {
          this.impersonationService.navigateBackToCapitalAccess();
        } else if (selectedItem.dummyUserId) {
          this.impersonationService.navigateToCapitalAccessWithImpersonation(selectedItem.dummyUserId, true);
        }
      } else {
        this.resetSelection();
      }
    });
  }

  private showConfirmationModal(selectedItem: ConsultantClientSelectorItem): Observable<boolean> {
    return this.localizationService
      .localize(
        [
          'consultantsClientSwitchClient',
          'consultantsClientReturnToMyAccount',
          'consultantsClientSwitchClientMessage',
          'consultantsClientReturnToMyAccountMessage',
          'authorize',
          'cancel'
        ],
        { accountName: selectedItem.name },
        this.localizationScope
      )
      .pipe(
        takeUntil(this.destroy$),
        take(1),
        switchMap(
          ([
            consultantsClientSwitchClient,
            consultantsClientReturnToMyAccount,
            consultantsClientSwitchClientMessage,
            consultantsClientReturnToMyAccountMessage,
            authorize,
            cancel
          ]) => {
            if (selectedItem.id === this.myAccountId) {
              return this.modalService
                .openConfirmation({
                  style: 'confirmation',
                  title: consultantsClientReturnToMyAccount,
                  body: consultantsClientReturnToMyAccountMessage,
                  actionText: authorize,
                  dismissText: cancel
                })
                .result.then(
                  () => true,
                  () => false
                );
            } else {
              return this.modalService
                .openConfirmation({
                  style: 'confirmation',
                  title: consultantsClientSwitchClient,
                  body: consultantsClientSwitchClientMessage,
                  actionText: authorize,
                  dismissText: cancel
                })
                .result.then(
                  () => true,
                  () => false
                );
            }
          }
        )
      );
  }

  clientsNameFn = (client: ConsultantClientSelectorItem): string => {
    return client.name;
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetSelection(): void {
    // Reset to current account
    this.suggesterClients$.pipe(takeUntil(this.destroy$), take(1)).subscribe(clients => {
      const myAccount = clients.find(c => c.id === this.myAccountId);
      if (myAccount) {
        this.selectedClient.set(myAccount);
      }
    });
  }
}
